import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { AppleButton } from "@/components/ui/apple-button";
import { Loader2, Calendar, Phone, Tag, Box, Building2, MapPin, Clock, FileText, DollarSign, User, Lock, QrCode, Plus, FlipHorizontal } from "lucide-react";
import { SupabaseClient } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AssetForm } from "@/components/forms/AssetForm";
import { AssetRecordForm } from "@/components/forms/AssetRecordForm";
import { AssetRecordsList } from "@/components/assets/AssetRecordsList";
import RegisterAssetModal from "@/components/ui/RegisterAssetModal";

type Asset = Database['public']['Tables']['assets']['Row'] & {
  asset_records: Database['public']['Tables']['asset_records']['Row'][];
};

interface QRScannerProps {
  onScan: (value: string) => void;
  isPublic?: boolean;
  supabaseClient: SupabaseClient<Database>;
}

export const QRScanner = ({ onScan, isPublic = false, supabaseClient }: QRScannerProps) => {
  const { t } = useTranslation();
  const [scanning, setScanning] = useState(false);
  const [html5QrCode, setHtml5QrCode] = useState<Html5Qrcode | null>(null);
  const [qrId, setQrId] = useState<string | null>(null);
  const [format, setFormat] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [asset, setAsset] = useState<Asset | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(false);
  const { toast } = useToast();
  const scannerRef = useRef<Html5Qrcode | null>(null);

  // Check for pending QR ID on mount
  useEffect(() => {
    const pendingUrl = localStorage.getItem('pendingQrId');
    if (pendingUrl) {
      setQrId(pendingUrl);
      onScan(pendingUrl);
      findAssetByQrId(pendingUrl, true);
      localStorage.removeItem('pendingQrId');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(err => {
          console.error("Error stopping QR scanner during cleanup:", err);
        });
        scannerRef.current = null;
      }
    };
  }, []);

  const findAssetByQrId = async (id: string, fromDirectUrl = false) => {
    if (!id.trim()) {
      toast({
        variant: "destructive",
        title: t('scanner.scanError'),
        description: "QR ID cannot be empty"
      });
      return;
    }

    setIsLoading(true);
    setAsset(null);
    try {
      const { data, error } = await supabaseClient
        .from("assets")
        .select(`
          *,
          asset_records(
            *
          )
        `)
        .eq("qr_id", id)
        .single();

      if (error || (isPublic && data?.private === true)) {
        setShowRegisterModal(true);
        return;
      }

      // Set the asset
      setAsset(data as Asset);
      
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('scanner.scanError'),
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startScanning = async () => {
    try {
      // Clear previous asset when starting new scan
      setAsset(null);
      
      // Set scanning to true first to render the div
      setScanning(true);
      
      // Small delay to ensure div is rendered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const scanner = new Html5Qrcode("qr-reader", {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODABAR,
          Html5QrcodeSupportedFormats.ITF,
          Html5QrcodeSupportedFormats.AZTEC,
          Html5QrcodeSupportedFormats.PDF_417,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
          Html5QrcodeSupportedFormats.MAXICODE,
          Html5QrcodeSupportedFormats.RSS_14
        ],
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        },
        verbose: false
      });

      scannerRef.current = scanner;
      setHtml5QrCode(scanner);

      // Get the screen dimensions
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      // Calculate qrbox size - make it responsive but with a max size
      const qrboxSize = Math.min(Math.min(screenWidth, screenHeight) * 0.7, 400);

      await scanner.start(
        { 
          facingMode: useFrontCamera ? "user" : "environment"
        },
        {
          fps: 30,
          qrbox: { width: qrboxSize, height: qrboxSize },
          aspectRatio: window.innerHeight > window.innerWidth ? 1.777778 : 1.333333, // 16:9 or 4:3
          disableFlip: false,
          videoConstraints: {
            width: { min: 640, ideal: screenWidth, max: 1920 },
            height: { min: 480, ideal: screenHeight, max: 1080 },
            facingMode: useFrontCamera ? "user" : "environment"
          }
        },
        async (decodedText, decodedResult) => {
          // Stop the scanner immediately after getting a code
          if (scannerRef.current) {
            try {
              await scannerRef.current.stop();
              scannerRef.current = null;
              setHtml5QrCode(null);
              setScanning(false);
            } catch (err) {
              console.error("Error stopping scanner after scan:", err);
            }
          }
          
          // Get the format from the result
          const format = decodedResult.result.format.formatName;
          
          // Then process the scanned code
          setFormat(format);
          setQrId(decodedText);
          onScan(decodedText);
          await findAssetByQrId(decodedText, false);
        },
        (error) => {
          if (!error.includes("QR code parse error") && !error.includes("No barcode detected")) {
            console.error("Scanner error:", error);
          }
        }
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
        setHtml5QrCode(null);
        setScanning(false);
      } catch (err) {
        console.error("Error stopping QR scanner:", err);
      }
    }
  };

  const toggleCamera = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
        setHtml5QrCode(null);
      } catch (err) {
        console.error("Error stopping scanner when toggling camera:", err);
      }
    }
    setUseFrontCamera(!useFrontCamera);
    startScanning();
  };

  const handleCreateAsset = () => {
    setShowCreateForm(true);
    setShowRegisterModal(false);
  };

  const handleCreateRecord = () => {
    setShowRecordDialog(true);
  };

  const handleRecordSuccess = () => {
    setShowRecordDialog(false);
    if (qrId) {
      findAssetByQrId(qrId);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-2 w-full">
        <AppleButton 
          variant={scanning ? "secondary" : "heartbeat"}
          className="flex-1 h-14 text-lg rounded-xl relative"
          onClick={scanning ? stopScanning : startScanning}
        >
          {scanning ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('buttons.scanning')}</span>
            </div>
          ) : (
            t('buttons.startScan')
          )}
        </AppleButton>
        {scanning && (
          <Button
            variant="outline"
            className="px-3 rounded-xl"
            onClick={toggleCamera}
            title={useFrontCamera ? "Switch to back camera" : "Switch to front camera"}
          >
            <FlipHorizontal className="h-5 w-5" />
          </Button>
        )}
      </div>

      {scanning && (
        <div 
          id="qr-reader" 
          className="w-full max-w-[600px] aspect-[4/3] rounded-2xl overflow-hidden bg-black"
          style={{
            minHeight: '300px',
            maxHeight: '80vh'
          }}
        />
      )}

      {isLoading && (
        <div className="w-full p-6 bg-gray-50 rounded-xl">
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
          </div>
        </div>
      )}

      {asset && !scanning && !isLoading && (
        <div className="w-full space-y-6">
          {/* Add Create Record Button at the top - only show if not public */}
          {!isPublic && (
            <AppleButton 
              onClick={handleCreateRecord}
              variant="secondary"
              className="w-full flex items-center justify-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>{t('buttons.createRecord')}</span>
            </AppleButton>
          )}

          {/* Asset Image */}
          {asset.images && (
            <div className="w-full aspect-square rounded-xl overflow-hidden bg-gray-100">
              <img 
                src={asset.images.split(',')[0]} 
                alt={asset.name || asset.asset_name || t('scanner.assetDetails.unnamedAsset')} 
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Asset Details */}
          <div className="w-full p-6 bg-white rounded-xl shadow-sm space-y-4">
            <h2 className="text-xl font-semibold">
              {asset.name || asset.asset_name || t('scanner.assetDetails.unnamedAsset')}
            </h2>

            <div className="grid grid-cols-1 gap-4">
              {/* Basic Info */}
              <div className="space-y-3">
                {asset.asset_description && (
                  <p className="text-sm text-gray-600">{asset.asset_description}</p>
                )}

                <div className="flex items-center space-x-2 text-sm">
                  <QrCode className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{t('scanner.assetDetails.qrId')}:</span>
                  <span className="font-mono">{asset.qr_id}</span>
                </div>

                {asset.serial_number && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Box className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.serialNumber')}:</span>
                    <span>{asset.serial_number}</span>
                  </div>
                )}

                {asset.category && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.category')}:</span>
                    <span>{asset.category}</span>
                  </div>
                )}

                {asset.location && (
                  <div className="flex items-center space-x-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.location')}:</span>
                    <span>{asset.location}</span>
                  </div>
                )}

                {asset.asset_price && (
                  <div className="flex items-center space-x-2 text-sm">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.price')}:</span>
                    <span>${asset.asset_price}</span>
                  </div>
                )}

                {asset.purchase_date && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.purchaseDate')}:</span>
                    <span>{new Date(asset.purchase_date).toLocaleDateString()}</span>
                  </div>
                )}

                {asset.warranty_expiry && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.warrantyExpiry')}:</span>
                    <span>{new Date(asset.warranty_expiry).toLocaleDateString()}</span>
                  </div>
                )}

                {asset.status && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.status')}:</span>
                    <span>{asset.status}</span>
                  </div>
                )}

                {asset.user_name && (
                  <div className="flex items-center space-x-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.user')}:</span>
                    <span>{asset.user_name}</span>
                  </div>
                )}

                {asset.phone && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{t('scanner.assetDetails.phone')}:</span>
                    <span>{asset.phone}</span>
                  </div>
                )}

                {asset.notes && (
                  <div className="flex items-start space-x-2 text-sm">
                    <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-gray-600">{t('scanner.assetDetails.notes')}:</span>
                      <p className="mt-1">{asset.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Asset Records */}
          {(!isPublic || (isPublic && asset.see_records)) && asset.asset_records && asset.asset_records.length > 0 && (
            <div className="w-full">
              <AssetRecordsList 
                records={asset.asset_records} 
                assetId={asset.id}
                onRecordAdded={() => findAssetByQrId(qrId || '')}
                isPublic={isPublic}
              />
            </div>
          )}

          {/* Add Record Button */}
          {(!isPublic || (isPublic && asset.add_records)) && (
            <div className="w-full">
              <AppleButton
                onClick={() => setShowRecordDialog(true)}
                variant="secondary"
                className="w-full flex items-center justify-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>{t('buttons.addRecord')}</span>
              </AppleButton>
            </div>
          )}
        </div>
      )}

      {qrId && !asset && !scanning && !isLoading && (
        <div className="w-full p-6 bg-gray-50 rounded-xl space-y-2">
          <p className="text-sm text-gray-500 text-center">{t('scanner.scannedId.qr')}:</p>
          <div className="max-w-full overflow-hidden">
            <p className="font-mono text-sm text-center text-gray-500 mb-1">{format || 'Unknown Format'}</p>
            <p className="font-mono text-sm text-center text-blue-600 break-all">{qrId}</p>
          </div>
        </div>
      )}

      <RegisterAssetModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        assetId={qrId || ''}
        scanType="QR"
        format={format || undefined}
        isPublic={isPublic}
        onRegister={handleCreateAsset}
      />

      {/* Create Asset Form Dialog */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('scanner.createAsset')}</DialogTitle>
              <DialogDescription>
                {t('scanner.createAssetDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {qrId && (
                <AssetForm 
                  mode="create" 
                  initialData={{ 
                    qr_id: qrId,
                    name: "",
                    asset_name: "",
                    status: "Available"
                  }}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Dialog */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('scanner.createRecord')}</DialogTitle>
              <DialogDescription>
                {t('scanner.createRecordDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {asset && (
                <AssetRecordForm
                  assetId={asset.id}
                  mode="create"
                  onSuccess={handleRecordSuccess}
                  onCancel={() => setShowRecordDialog(false)}
                  isPublic={isPublic}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 